import { useState, useEffect } from "react";
import { showAlert } from '@/components/AppDialog';
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { ACCENT, SHELL_BG } from "../components/AppShell";
import MobileNav from "../components/MobileNav";
import UserProfileMenu from "../components/UserProfileMenu";
import { API_BASE_URL } from '@/utils/env';

const YEARS = [2026, 2025, 2024, 2023];

type KpiTab = 'unit' | 'retention' | 'growth' | 'liquidity';

interface UnitEcon { arpu: string; grossMargin: string; monthlyChurn: string; cac: string; }
interface Retention { planPrice: string; activeSubs: string; startingMrr: string; expansionMrr: string; contractionMrr: string; churnedMrr: string; }
interface Growth { arrCurrent: string; arrPrev: string; salesMarketing: string; netNewArr: string; }
interface Liquidity { cashOutflows: string; cashInflows: string; currentCash: string; }

const EMPTY_UNIT: UnitEcon = { arpu: '', grossMargin: '', monthlyChurn: '', cac: '' };
const EMPTY_RETENTION: Retention = { planPrice: '', activeSubs: '', startingMrr: '', expansionMrr: '', contractionMrr: '', churnedMrr: '' };
const EMPTY_GROWTH: Growth = { arrCurrent: '', arrPrev: '', salesMarketing: '', netNewArr: '' };
const EMPTY_LIQUIDITY: Liquidity = { cashOutflows: '', cashInflows: '', currentCash: '' };

function n(s: string) { return parseFloat(s) || 0; }

function DerivedField({ label, value, unit, info }: { label: string; value: string; unit?: string; info?: string }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5">
        <label className="block text-xs font-medium text-[#374151]">{label}</label>
        {info && (
          <button type="button" onClick={() => setShowInfo(v => !v)}
            className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] flex items-center justify-center hover:bg-gray-300">i</button>
        )}
      </div>
      {showInfo && info && <p className="text-xs text-gray-500 mb-1.5 bg-gray-50 rounded p-2">{info}</p>}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#E2F4EA] border border-[#b2dfc6] rounded-lg">
        <span className="text-sm font-semibold text-[#15824E]">{value}</span>
        {unit && <span className="text-xs text-[#15824E]">{unit}</span>}
      </div>
    </div>
  );
}

function NumInput({ label, value, onChange, unit, info, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; unit?: string; info?: string; placeholder?: string;
}) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5">
        <label className="block text-xs font-medium text-[#374151]">{label}</label>
        {unit && <span className="text-xs text-gray-400">{unit}</span>}
        {info && (
          <button type="button" onClick={() => setShowInfo(v => !v)}
            className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] flex items-center justify-center hover:bg-gray-300">i</button>
        )}
      </div>
      {showInfo && info && <p className="text-xs text-gray-500 mb-1.5 bg-gray-50 rounded p-2">{info}</p>}
      <input type="number" min="0" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || '0'}
        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none" />
    </div>
  );
}

/* Figures are entered and shown in dirhams — no second currency, no conversion. */
function fmt(v: number) {
  return `AED ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export default function KPIsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<KpiTab>('unit');
  const [period, setPeriod] = useState(YEARS[0]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [unit, setUnit] = useState<UnitEcon>(EMPTY_UNIT);
  const [retention, setRetention] = useState<Retention>(EMPTY_RETENTION);
  const [growth, setGrowth] = useState<Growth>(EMPTY_GROWTH);
  const [liquidity, setLiquidity] = useState<Liquidity>(EMPTY_LIQUIDITY);
  // Full { [year]: {...} } map — kept so a save writes a proper nested object
  // (backend RTDB update() rejects dotted keys like "kpis.2026").
  const [allKpis, setAllKpis] = useState<Record<string, any>>({});

  useEffect(() => {
    (async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) { navigate('/login'); return; }
        const user = JSON.parse(userStr);
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) }
        });
        if (resp.ok) {
          const json = await resp.json();
          const kmap = (json.user?.kpis && typeof json.user.kpis === 'object') ? json.user.kpis : {};
          setAllKpis(kmap);
          const kpis = kmap[period];
          setUnit(kpis?.unit ? { ...EMPTY_UNIT, ...kpis.unit } : EMPTY_UNIT);
          setRetention(kpis?.retention ? { ...EMPTY_RETENTION, ...kpis.retention } : EMPTY_RETENTION);
          setGrowth(kpis?.growth ? { ...EMPTY_GROWTH, ...kpis.growth } : EMPTY_GROWTH);
          setLiquidity(kpis?.liquidity ? { ...EMPTY_LIQUIDITY, ...kpis.liquidity } : EMPTY_LIQUIDITY);
        }
      } catch (e) { console.error(e); }
      finally { setFetching(false); }
    })();
  }, [navigate, period]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/login'); return; }
      const user = JSON.parse(userStr);
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      const nextKpis = { ...allKpis, [period]: { unit, retention, growth, liquidity, updatedAt: new Date().toISOString() } };
      const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ kpis: nextKpis })
      });
      if (!resp.ok) throw new Error('Failed to save');
      setAllKpis(nextKpis);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
      showAlert('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => { localStorage.removeItem('user'); navigate('/'); };

  // Auto-derived: Unit Economics
  const ltv = n(unit.monthlyChurn) > 0 ? n(unit.arpu) / (n(unit.monthlyChurn) / 100) : 0;
  const payback = n(unit.arpu) > 0 ? n(unit.cac) / n(unit.arpu) : 0;
  const ltvCac = n(unit.cac) > 0 ? ltv / n(unit.cac) : 0;

  // Auto-derived: Retention
  const mrr = n(retention.planPrice) * n(retention.activeSubs);
  const nrr = n(retention.startingMrr) > 0
    ? ((n(retention.startingMrr) + n(retention.expansionMrr) - n(retention.contractionMrr)) / n(retention.startingMrr)) * 100 : 0;
  const mrrChurnPct = n(retention.startingMrr) > 0 ? (n(retention.churnedMrr) / n(retention.startingMrr)) * 100 : 0;

  // Auto-derived: Growth
  const magicNumber = n(growth.salesMarketing) > 0
    ? ((n(growth.arrCurrent) - n(growth.arrPrev)) / n(growth.salesMarketing)) : 0;
  const burnMultiple = n(growth.netNewArr) > 0
    ? (n(growth.salesMarketing) / n(growth.netNewArr)) : 0;

  // Auto-derived: Liquidity
  const netBurn = n(liquidity.cashOutflows) - n(liquidity.cashInflows);
  const runway = netBurn > 0 ? n(liquidity.currentCash) / netBurn : 0;

  const TABS: { key: KpiTab; label: string }[] = [
    { key: 'unit', label: 'Unit economics' },
    { key: 'retention', label: 'Retention & churn' },
    { key: 'growth', label: 'Growth & efficiency' },
    { key: 'liquidity', label: 'Liquidity & runway' },
  ];

  if (fetching) return (
    <div className="min-h-screen" style={{ background: SHELL_BG }}>
      <Sidebar onLogout={handleSignOut} />
      <main className="md:ms-64 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: ACCENT, borderTopColor: "transparent" }} />
      </main>
    </div>
  );

  return (
    <div className="min-h-screen text-slate-900" style={{ background: SHELL_BG }}>
      <Sidebar onLogout={handleSignOut} />
      <main className="md:ms-64 flex-1 overflow-y-auto relative min-h-screen" style={{ background: SHELL_BG }}>
        <MobileNav onLogout={handleSignOut} />
        <div className="absolute top-6 end-8 z-10"><UserProfileMenu /></div>

        <div className="mx-auto max-w-[760px] px-6 py-12 pb-28">
          <div className="mb-2 flex items-center gap-1 text-sm text-[#6B7280]">
            <button onClick={() => navigate('/dashboard')} className="hover:text-gray-900">My Applications</button>
            <span>/</span>
            <span className="text-[#111827] font-medium">KPIs</span>
          </div>

          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold text-[#111827] mb-1">KPIs</h1>
              <p className="text-sm text-[#6B7280]">Track your key performance indicators by period.</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Period selector */}
              <select value={period} onChange={e => setPeriod(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white acc-ring focus:outline-none">
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition whitespace-nowrap flex-shrink-0 ${tab === t.key ? 'bg-white text-[#111827] shadow-sm' : 'text-[#6B7280] hover:text-[#111827]'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Unit Economics */}
          {tab === 'unit' && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="text-sm font-semibold text-[#111827]">Unit economics</div>
                <div className="text-xs text-[#6B7280]">Per-customer revenue and cost efficiency</div>
              </div>
              <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <NumInput label="ARPU" value={unit.arpu} onChange={v => setUnit(p => ({ ...p, arpu: v }))} unit="/mo" placeholder="Average revenue per user" info="Average monthly revenue per active user." />
                <NumInput label="Gross margin" value={unit.grossMargin} onChange={v => setUnit(p => ({ ...p, grossMargin: v }))} unit="%" placeholder="e.g. 70" info="(Revenue − COGS) / Revenue × 100" />
                <NumInput label="Monthly churn" value={unit.monthlyChurn} onChange={v => setUnit(p => ({ ...p, monthlyChurn: v }))} unit="%" placeholder="e.g. 2" info="% of customers lost each month." />
                <NumInput label="CAC" value={unit.cac} onChange={v => setUnit(p => ({ ...p, cac: v }))} placeholder="Customer acquisition cost" info="Total sales & marketing spend / new customers acquired." />
                <DerivedField label="LTV" value={ltv > 0 ? fmt(ltv) : '-'} info="ARPU ÷ Monthly churn %" />
                <DerivedField label="Payback period" value={payback > 0 ? `${payback.toFixed(1)} mo` : '-'} info="CAC ÷ ARPU" />
                <DerivedField label="LTV : CAC ratio" value={ltvCac > 0 ? `${ltvCac.toFixed(1)}×` : '-'} info="LTV ÷ CAC. Target ≥ 3×." />
              </div>
            </div>
          )}

          {/* Retention & Churn */}
          {tab === 'retention' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="text-sm font-semibold text-[#111827]">Recurring revenue</div>
                </div>
                <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumInput label="Plan price" value={retention.planPrice} onChange={v => setRetention(p => ({ ...p, planPrice: v }))} unit="/mo" placeholder="Price per subscription" />
                  <NumInput label="Active subscriptions" value={retention.activeSubs} onChange={v => setRetention(p => ({ ...p, activeSubs: v }))} placeholder="Number of active subs" />
                  <DerivedField label="MRR" value={mrr > 0 ? fmt(mrr) : '-'} unit="/mo" info="Plan price × active subscriptions." />
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="text-sm font-semibold text-[#111827]">Retention</div>
                </div>
                <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumInput label="Starting MRR" value={retention.startingMrr} onChange={v => setRetention(p => ({ ...p, startingMrr: v }))} placeholder="MRR at start of period" />
                  <NumInput label="Expansion MRR" value={retention.expansionMrr} onChange={v => setRetention(p => ({ ...p, expansionMrr: v }))} placeholder="Upgrades / upsells" />
                  <NumInput label="Contraction MRR" value={retention.contractionMrr} onChange={v => setRetention(p => ({ ...p, contractionMrr: v }))} placeholder="Downgrades" />
                  <NumInput label="Churned MRR" value={retention.churnedMrr} onChange={v => setRetention(p => ({ ...p, churnedMrr: v }))} placeholder="Lost MRR from cancellations" />
                  <DerivedField label="Net Revenue Retention" value={nrr > 0 ? `${nrr.toFixed(1)}%` : '-'} info="(Starting + Expansion − Contraction) / Starting × 100. Target ≥ 100%." />
                  <DerivedField label="MRR churn" value={mrrChurnPct > 0 ? `${mrrChurnPct.toFixed(1)}%` : '-'} info="Churned MRR / Starting MRR × 100." />
                </div>
              </div>
            </div>
          )}

          {/* Growth & Efficiency */}
          {tab === 'growth' && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="text-sm font-semibold text-[#111827]">Growth & efficiency</div>
                <div className="text-xs text-[#6B7280]">How efficiently you're growing ARR</div>
              </div>
              <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <NumInput label="ARR: current quarter" value={growth.arrCurrent} onChange={v => setGrowth(p => ({ ...p, arrCurrent: v }))} placeholder="Annual recurring revenue (now)" />
                <NumInput label="ARR: previous quarter" value={growth.arrPrev} onChange={v => setGrowth(p => ({ ...p, arrPrev: v }))} placeholder="Annual recurring revenue (prior)" />
                <NumInput label="Sales & marketing spend" value={growth.salesMarketing} onChange={v => setGrowth(p => ({ ...p, salesMarketing: v }))} placeholder="Total S&M spend" />
                <NumInput label="Net new ARR" value={growth.netNewArr} onChange={v => setGrowth(p => ({ ...p, netNewArr: v }))} placeholder="New ARR added this period" />
                <DerivedField label="Magic number" value={magicNumber > 0 ? `${magicNumber.toFixed(2)}×` : '-'} info="(ARR current − ARR previous) ÷ S&M spend. Target ≥ 0.75×." />
                <DerivedField label="Burn multiple" value={burnMultiple > 0 ? `${burnMultiple.toFixed(2)}×` : '-'} info="S&M spend ÷ Net new ARR. Lower is better." />
              </div>
            </div>
          )}

          {/* Liquidity & Runway */}
          {tab === 'liquidity' && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="text-sm font-semibold text-[#111827]">Liquidity & runway</div>
                <div className="text-xs text-[#6B7280]">Cash position and survival horizon</div>
              </div>
              <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <NumInput label="Cash outflows" value={liquidity.cashOutflows} onChange={v => setLiquidity(p => ({ ...p, cashOutflows: v }))} unit="/mo" placeholder="Monthly cash spent" />
                <NumInput label="Cash inflows" value={liquidity.cashInflows} onChange={v => setLiquidity(p => ({ ...p, cashInflows: v }))} unit="/mo" placeholder="Monthly cash received" />
                <NumInput label="Current cash balance" value={liquidity.currentCash} onChange={v => setLiquidity(p => ({ ...p, currentCash: v }))} placeholder="Current cash on hand" info="Shared with Financials page." />
                <DerivedField label="Net burn" value={netBurn > 0 ? fmt(netBurn) : '-'} unit="/mo" info="Cash outflows − cash inflows." />
                <DerivedField label="Runway" value={runway > 0 ? `${runway.toFixed(1)} mo` : '-'} info="Current cash ÷ net burn." />
              </div>
            </div>
          )}
        </div>

        {/* Fixed save bar */}
        <div className="fixed bottom-0 md:left-64 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-3 z-20">
          {saved && (
            <span className="text-sm text-green-600 font-medium flex items-center gap-1">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Saved
            </span>
          )}
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-[#374151] border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading}
            className="btn-acc px-5 py-2 text-sm font-medium rounded-lg transition">
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </main>
    </div>
  );
}
